import { useState, useCallback, useEffect } from "react";
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  Loader2,
  AlertTriangle,
  Eye,
  EyeOff,
  Terminal,
  Zap,
  RefreshCw,
  Clock,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useApiKeys, useCreateApiKey, useRevokeApiKey, useRotateApiKey } from "@/hooks/useApiKeys";
import { API_KEY_EXPIRY_OPTIONS } from "@/constants/apiKeys";
import { toast } from "sonner";

function formatDate(dateString: string | null): string {
  if (!dateString) return "Never";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

function RelativeTime({ date }: { date: string | null }) {
  if (!date) return <span className="text-muted-foreground">Never</span>;
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return <span>Today</span>;
  if (diffDays === 1) return <span>Yesterday</span>;
  if (diffDays < 30) return <span>{diffDays}d ago</span>;
  return <span>{formatDate(date)}</span>;
}

function ExpiryBadge({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) {
    return <span className="text-muted-foreground text-sm">Never</span>;
  }

  const now = new Date();
  const expires = new Date(expiresAt);
  const diffMs = expires.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) {
    return (
      <Badge variant="destructive" className="text-xs gap-1">
        <Clock className="h-3 w-3" />
        Expired
      </Badge>
    );
  }

  if (diffDays <= 7) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="destructive" className="text-xs gap-1 cursor-default">
              <Clock className="h-3 w-3" />
              {diffDays}d left
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{formatDate(expiresAt)}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (diffDays <= 30) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge
              variant="outline"
              className="text-xs gap-1 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700 cursor-default"
            >
              <Clock className="h-3 w-3" />
              {diffDays}d left
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{formatDate(expiresAt)}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <span className="text-muted-foreground text-sm cursor-default">
            {formatDate(expiresAt)}
          </span>
        </TooltipTrigger>
        <TooltipContent>{diffDays} days remaining</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function SetupStep({
  step,
  title,
  command,
  icon,
}: {
  step: number;
  title: string;
  command: string;
  icon?: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Failed to copy — please select and copy manually");
    }
  }, [command]);

  return (
    <li className="ml-6">
      <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold ring-4 ring-background">
        {icon || step}
      </span>
      <p className="text-sm font-medium mb-1.5 pt-0.5">{title}</p>
      <div className="group relative">
        <pre className="overflow-x-auto rounded-[10px] border bg-muted/60 px-3 py-2 pr-10 font-mono text-xs">
          {command}
        </pre>
        <button
          onClick={handleCopy}
          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-opacity rounded p-1 hover:bg-muted-foreground/10"
          aria-label="Copy command"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-600" />
          ) : (
            <Copy className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </button>
      </div>
    </li>
  );
}

export function ApiKeysSection() {
  const { data: keys, isLoading, isError, error, refetch } = useApiKeys();
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();
  const rotateKey = useRotateApiKey();

  const [newKeyName, setNewKeyName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState<string>("none");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{ uuid: string; name: string } | null>(null);
  const [rotateTarget, setRotateTarget] = useState<{ uuid: string; name: string } | null>(null);

  useEffect(() => {
    if (!createdKey) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [createdKey]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      const result = await createKey.mutateAsync({
        name: newKeyName.trim(),
        expires_in_days: expiresInDays === "none" ? null : Number(expiresInDays),
      });
      setCreatedKey(result.key);

      setNewKeyName("");
      setExpiresInDays("none");
      setShowKey(true);
    } catch {
      toast.error("Failed to create API key");
    }
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      toast.success("API key copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy — please select and copy manually");
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeKey.mutateAsync(revokeTarget.uuid);
      toast.success(`Revoked key "${revokeTarget.name}"`);
    } catch {
      toast.error("Failed to revoke API key");
    } finally {
      setRevokeTarget(null);
    }
  };

  const handleRotate = async () => {
    if (!rotateTarget) return;
    try {
      const result = await rotateKey.mutateAsync(rotateTarget.uuid);
      setCreatedKey(result.key);

      setShowKey(true);
      toast.success(`Rotated key "${rotateTarget.name}" — copy the new key now`);
    } catch {
      toast.error("Failed to rotate API key");
    } finally {
      setRotateTarget(null);
    }
  };

  const dismissCreatedKey = () => {
    setCreatedKey(null);

    setShowKey(false);
    setCopied(false);
  };

  return (
    <div className="space-y-6">
      {/* Create Key */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            API Keys
          </CardTitle>
          <CardDescription>
            Create keys to authenticate with the Vortal API and Claude Code plugin.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Input
              maxLength={100}
              placeholder="Key name, e.g. Claude Code"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !createKey.isPending && handleCreate()}
              className="max-w-sm"
              disabled={createKey.isPending}
            />
            <Select value={expiresInDays} onValueChange={setExpiresInDays}>
              <SelectTrigger className="w-[160px] rounded-full bg-white">
                <SelectValue placeholder="Expiration" />
              </SelectTrigger>
              <SelectContent className="rounded-[20px] bg-white">
                {API_KEY_EXPIRY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleCreate} disabled={!newKeyName.trim() || createKey.isPending}>
              {createKey.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Create Key
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Newly Created Key (shown once) */}
      {createdKey && (
        <Card className="rounded-[15px] border-green-200 bg-green-50/50 shadow-none dark:border-green-800 dark:bg-green-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-green-700 dark:text-green-400">
              <Check className="h-4 w-4" />
              API Key Created
            </CardTitle>
            <CardDescription>Copy this key now — it won't be shown again.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <code className="flex-1 select-all break-all rounded-[10px] border bg-background px-3 py-2 font-mono text-sm">
                {showKey ? createdKey : "\u2022".repeat(40)}
              </code>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowKey(!showKey)}
                className="shrink-0"
                aria-label={showKey ? "Hide API key" : "Show API key"}
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0">
                {copied ? (
                  <Check className="mr-2 h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="mr-2 h-4 w-4" />
                )}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button variant="ghost" size="sm" onClick={dismissCreatedKey} className="shrink-0">
                Dismiss
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Use this key as your <code className="text-xs">VORTAL_API_KEY</code> environment
              variable.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Key List */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle className="text-base">Your keys</CardTitle>
          <CardDescription>
            Active API keys for your account. Rotate or revoke any key you no longer need.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertTriangle className="h-10 w-10 text-destructive/60 mb-3" />
              <p className="text-sm text-destructive mb-3">
                {error instanceof Error ? error.message : "Failed to load API keys."}
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : keys && keys.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                        {key.prefix}...
                      </code>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(key.created_at)}
                    </TableCell>
                    <TableCell>
                      <ExpiryBadge expiresAt={key.expires_at} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      <RelativeTime date={key.last_used_at} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRotateTarget({ uuid: key.uuid, name: key.name })}
                                disabled={rotateKey.isPending && rotateTarget?.uuid === key.uuid}
                                aria-label={`Rotate key ${key.name}`}
                              >
                                {rotateKey.isPending && rotateTarget?.uuid === key.uuid ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Rotate key</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRevokeTarget({ uuid: key.uuid, name: key.name })}
                                disabled={revokeKey.isPending && revokeTarget?.uuid === key.uuid}
                                className="text-destructive hover:text-destructive"
                                aria-label={`Revoke key ${key.name}`}
                              >
                                {revokeKey.isPending && revokeTarget?.uuid === key.uuid ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Revoke key</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Key className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                No API keys yet. Create one to get started with the Claude Code plugin.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage Guide */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            Quick Setup
          </CardTitle>
          <CardDescription>Use your API key with the Vortal Claude Code plugin</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="relative border-l border-border ml-3 space-y-6">
            <SetupStep
              step={1}
              title="Set your environment variable"
              command='export VORTAL_API_KEY="ec_your_key_here"'
            />
            <SetupStep
              step={2}
              title="Add the MCP server to Claude Code"
              command="claude mcp add vortal -- npx @vortal/mcp-server"
            />
            <SetupStep
              step={3}
              title="Start scanning"
              command="/vortal:scan"
              icon={<Zap className="h-3 w-3" />}
            />
          </ol>
        </CardContent>
      </Card>

      {/* Revoke Confirmation */}
      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Revoke API Key?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently revoke{" "}
              <span className="font-semibold">&quot;{revokeTarget?.name}&quot;</span>. Any
              integrations using this key will stop working immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revokeKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rotate Confirmation */}
      <AlertDialog
        open={!!rotateTarget}
        onOpenChange={(open) => {
          if (!open) setRotateTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Rotate API Key?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new key and revoke{" "}
              <span className="font-semibold">&quot;{rotateTarget?.name}&quot;</span>. Update your
              integrations with the new key immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRotate}>
              {rotateKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Rotate Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
